import React, { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Input, UiButton } from '@shared/ui';
import { getEstimateInformationQuery } from '@widgets/EstimateInformation/api/hooks/getEstimateInformationQuery';
import { useCreateEstimateUserQuery } from '@widgets/EstimateInformation/api/hooks/useCreateEstimateUserQuery';

import cls from './ExistInput.module.css';

interface AddInputProps {
    classes?: string;
    modalMode?: 'managers' | 'purchasers' | null;
    onChangeInputMode: (value: 'add' | 'exist' | null) => void;
    onSetAddingUserEmail?: (value: string | null) => void;
    value: string;
    onInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSetEmailError: () => void;
    error: string | null;
}

const EMAIL_REGEX =
    /^(?!.*\.\.)(?!.*\.$)[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

export const ExistInput = ({
    classes,
    onChangeInputMode,
    modalMode,
    onSetAddingUserEmail,
    value,
    onInput,
    onSetEmailError,
    error,
}: AddInputProps) => {
    const { t } = useTranslation();

    const createEstimateUserMutation = useCreateEstimateUserQuery();

    const { refetch } = getEstimateInformationQuery();

    const inputRef = useRef<HTMLInputElement | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            if (!rootRef.current) return;

            const target = e.target as Node;

            if (!rootRef.current.contains(target)) {
                onChangeInputMode(null);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [onChangeInputMode]);

    const onExist = async (email: string) => {
        const isValidEmail = EMAIL_REGEX.test(value.trim());

        if (!isValidEmail) {
            onSetEmailError();
            return;
        }

        const formData = new FormData();

        formData.append('role', modalMode === 'managers' ? 'manager' : 'purchase_agent');
        formData.append('email', email);

        await createEstimateUserMutation.mutateAsync(formData);
        await refetch();
        onChangeInputMode(null);
        onSetAddingUserEmail(email);
    };

    return (
        <div ref={rootRef} className={cn(cls.ExistInput, classes)}>
            <Input
                error={error}
                maxLength={85}
                ref={inputRef}
                // onBlur={onBlur}
                value={value}
                onInput={onInput}
                placeholder={t('ExistInput.return.ExistInput.Input.placeholder')}
                inputClassName={cls.input}
                type="email"
            />
            <UiButton
                className={cls.existButton}
                btnText={t('ExistInput.return.ExistInput.UiButton.btnText')}
                disabled={!value}
                onMouseDown={async (e) => {
                    e.stopPropagation();
                    await onExist(value);
                }}
            />
        </div>
    );
};
